import { supabase } from './config';
import { callEdgeFunction, EdgeFunctionError } from './edgeClient';
import { createLatestFetcher } from './realtimeFetch';

// Convert a dispatch.work_orders row to the shape the UI uses.
// `missingComponents` is only set when the column exists; otherwise it stays undefined
// and the UI falls back to the flag from the last Priority fetch.
export const mapWorkOrderRow = (wo) => ({
  serialName: wo.serial_name,
  partName: wo.part_name,
  partDescription: wo.part_description,
  quantity: wo.quantity,
  reportedQuantity: wo.reported_quantity || 0,
  remainingQuantity: wo.quantity - (wo.reported_quantity || 0),
  activityName: wo.activity_name,
  processName: wo.process_name,
  processDescription: wo.process_description,
  salesOrder: wo.sales_order,
  customerCode: wo.customer_code,
  customerName: wo.customer_name,
  plannedDate: wo.planned_date,
  endDate: wo.end_date,
  status: wo.status,
  priorityStatus: wo.priority_status,
  fetchedAt: wo.fetched_at,
  reportNo: wo.report_no,
  reportedAt: wo.reported_at,
  missingComponents: wo.missing_components ?? undefined
});

// Message for a failed GET-style edge call, matching the previous
// `responseData.error || "<fallback>: <status>"` wording.
const httpMessage = (error, fallback) => (
  error instanceof EdgeFunctionError && !error.body?.error
    ? `${fallback}: ${error.status}`
    : (error.message || fallback)
);

// ============================================
// PRODUCTION REPORT FUNCTIONS
// ============================================

// Fetch production employees from Priority ERP
export const fetchProductionEmployees = async () => {
  try {
    const responseData = await callEdgeFunction('fetch-production-employees', { method: 'GET' });

    return {
      success: true,
      employees: responseData.employees || []
    };
  } catch (error) {
    console.error('Error fetching production employees:', error);
    return {
      success: false,
      message: httpMessage(error, 'Failed to fetch employees'),
      employees: []
    };
  }
};

// Fetch reject reason codes from Priority ERP (DEFECTCODES).
// Rejects can be reported without a reason code, so a failure here degrades to an empty
// list and the selector simply stays hidden rather than blocking the operator.
export const fetchDefectCodes = async () => {
  try {
    const responseData = await callEdgeFunction('fetch-defect-codes', { method: 'GET' });

    return {
      success: true,
      defectCodes: responseData.defectCodes || []
    };
  } catch (error) {
    console.error('Error fetching defect codes:', error);
    return {
      success: false,
      message: httpMessage(error, 'Failed to fetch defect codes'),
      defectCodes: []
    };
  }
};

// Current Priority stock (LOGPART part balances) for a list of part numbers, shown beside
// frame and mount work orders. Informational only, so a failure returns no balances and
// the UI hides the figure rather than getting in the way of reporting.
export const fetchPartBalances = async (parts) => {
  try {
    const responseData = await callEdgeFunction('fetch-part-balances', { body: { parts } });

    return {
      success: true,
      balances: responseData.balances || {},
      fetchedAt: responseData.fetchedAt || new Date().toISOString()
    };
  } catch (error) {
    console.error('Error fetching part balances:', error);
    return {
      success: false,
      message: httpMessage(error, 'Failed to fetch stock'),
      balances: {}
    };
  }
};

// App-wide switch for the shelf count asked for when reporting frames / mounts.
// Defaults to on when it can't be read: asking for a count is the safe side.
export const SHELF_COUNT_SETTING = 'production_shelf_count';

export const getShelfCountSetting = async () => {
  const { data, error } = await supabase
    .from('app_settings')
    .select('value')
    .eq('key', SHELF_COUNT_SETTING)
    .maybeSingle();
  if (error) {
    console.error('Error reading shelf count setting:', error);
    return { success: false, enabled: true };
  }
  return { success: true, enabled: data ? data.value !== false : true };
};

// Settings page: check the settings password (only the Edge Function knows it).
export const unlockSettings = async (password) => {
  try {
    await callEdgeFunction('app-settings', { body: { action: 'unlock', password } });
    return { success: true };
  } catch (error) {
    return { success: false, message: error?.status === 401 ? 'Wrong password' : httpMessage(error, 'Could not check the password') };
  }
};

// Needs the settings password again - the server checks it on every change.
export const setShelfCountSetting = async (enabled, password) => {
  try {
    await callEdgeFunction('app-settings', { body: { key: SHELF_COUNT_SETTING, value: enabled, password } });
    return { success: true };
  } catch (error) {
    console.error('Error saving shelf count setting:', error);
    return { success: false, message: httpMessage(error, 'Could not save the setting') };
  }
};

// Shelf counts logged by the stock-count function since `since` (a Date), newest first.
export const SHELF_COUNT_LOG_LIMIT = 2000;

export const fetchShelfCountLog = async (since) => {
  const { data, error } = await supabase
    .from('stock_count_checks')
    .select('id, created_at, user_email, employee_name, part_name, serial_names, location, priority_balance, picked_not_shipped, expected, counted, first_count, difference, status, doc_no, error_message')
    .gte('created_at', since.toISOString())
    .order('created_at', { ascending: false })
    .limit(SHELF_COUNT_LOG_LIMIT);
  if (error) {
    console.error('Error reading the shelf count log:', error);
    return { success: false, rows: [], message: 'Could not load the shelf count log' };
  }
  const rows = data || [];
  // Product descriptions come from the work orders the counts were taken on. A failed
  // lookup must never hide the log, so it only ever adds descriptions.
  const parts = [...new Set(rows.map(r => r.part_name).filter(Boolean))];
  if (parts.length) {
    const descriptions = new Map();
    for (let i = 0; i < parts.length; i += 200) {
      const { data: wos, error: woError } = await supabase
        .from('work_orders')
        .select('part_name, part_description')
        .in('part_name', parts.slice(i, i + 200))
        .not('part_description', 'is', null);
      if (woError) { console.warn('Could not load part descriptions:', woError); break; }
      for (const wo of wos || []) {
        if (wo.part_description && !descriptions.has(wo.part_name)) descriptions.set(wo.part_name, wo.part_description);
      }
    }
    return { success: true, rows: rows.map(r => ({ ...r, part_description: descriptions.get(r.part_name) || null })) };
  }
  return { success: true, rows };
};

// Send shelf counts taken before reporting. Priority's stock is corrected by the
// difference; a large difference comes back as needsRecount and nothing is changed.
// counts: [{ partName, counted, firstCount?, serialNames }]
export const submitShelfCount = async (counts, employeeName) => {
  try {
    const responseData = await callEdgeFunction('stock-count', { body: { counts, employeeName } });
    return {
      success: Boolean(responseData.success),
      needsRecount: responseData.needsRecount || [],
      results: responseData.results || []
    };
  } catch (error) {
    console.error('Error submitting shelf count:', error);
    return {
      success: false,
      needsRecount: [],
      results: [],
      message: httpMessage(error, 'Could not check the shelf count')
    };
  }
};

// Fetch released work orders from Priority ERP
export const fetchReleasedWorkOrders = async () => {
  try {
    const responseData = await callEdgeFunction('fetch-released-work-orders');

    return {
      success: true,
      workOrders: responseData.workOrders || [],
      count: responseData.count || 0,
      message: `Fetched ${responseData.count || 0} released work orders`
    };
  } catch (error) {
    console.error('Error fetching work orders:', error);
    return {
      success: false,
      message: httpMessage(error, 'Failed to fetch work orders'),
      workOrders: []
    };
  }
};

// Submit production report to Priority ERP
// `batchKey` is an optional idempotency key. It is stamped on the Priority report header
// (AFORM.DETAILS), so re-submitting the same batch after a timeout finds the existing
// report instead of reporting production twice.
export const submitProductionReport = async (items, batchKey) => {
  try {
    // Validate input
    if (!items || !Array.isArray(items) || items.length === 0) {
      throw new Error('No items provided for production report');
    }

    // Validate each item
    for (const item of items) {
      if (!item.serialName) {
        throw new Error('Each item must have a serialName');
      }
      if (!item.activityName) {
        throw new Error(`Item ${item.serialName} is missing activityName`);
      }
      // Allow a reject-only report (quantity 0 with a positive reject quantity), matching
      // the Edge Function's contract. Only block when there is neither a good nor reject qty.
      const qty = Number(item.quantity) || 0;
      const rejectQty = Number(item.rejectQuantity) || 0;
      if (qty <= 0 && rejectQty <= 0) {
        throw new Error(`Item ${item.serialName} must have a positive quantity or reject quantity`);
      }
    }

    let responseData;
    try {
      // 207 Multi-Status (partial success) is a 2xx and comes back here as a body.
      responseData = await callEdgeFunction('submit-production-report', {
        body: batchKey ? { items, batchKey } : { items }
      });
    } catch (error) {
      if (!(error instanceof EdgeFunctionError)) throw error;
      const errorData = error.body || {};
      console.error('Production report submission failed:', errorData);
      return {
        success: false,
        // 409: the same batch is still being processed by an earlier attempt.
        isConflict: error.status === 409,
        message: errorData.error || errorData.message || `Submission failed: ${error.status}`,
        reportNo: errorData.reportNo,
        lineResults: errorData.lineResults,
        updatedQuantities: errorData.updatedQuantities,
        workOrderUpdateFailures: errorData.workOrderUpdateFailures || []
      };
    }

    return {
      success: responseData.success,
      reportNo: responseData.reportNo,
      itemsReported: responseData.itemsReported,
      itemsFailed: responseData.itemsFailed || 0,
      totalQuantity: responseData.totalQuantity,
      updatedQuantities: responseData.updatedQuantities,
      message: responseData.message || 'Production report submitted successfully',
      lineResults: responseData.lineResults,
      materialWarning: responseData.materialWarning || null,
      // Work orders reported in Priority whose local row couldn't be updated
      workOrderUpdateFailures: responseData.workOrderUpdateFailures || [],
      duplicate: responseData.duplicate === true
    };
  } catch (error) {
    console.error('Error submitting production report:', error);
    return {
      success: false,
      message: error.message || 'Failed to submit production report',
      isNetworkError: true
    };
  }
};

// Get production report history from database
export const getProductionHistory = async (limit = 50) => {
  try {
    const { data, error } = await supabase
      .from('production_reports')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      throw error;
    }

    return {
      success: true,
      reports: data || [],
      message: `Fetched ${data?.length || 0} production reports`
    };
  } catch (error) {
    console.error('Error fetching production history:', error);
    return {
      success: false,
      message: error.message || 'Failed to fetch production history',
      reports: []
    };
  }
};

// Subscribe to work orders in realtime (pending AND partial status)
// All users see the same data - webhook syncs from Priority, realtime pushes to all clients.
// `onError` is called when a (re)fetch fails so the caller can stop its loading state.
export const subscribeToWorkOrders = (callback, onError) => {
  const fetcher = createLatestFetcher(
    () => supabase
      .from('work_orders')
      .select('*')
      .in('status', ['pending', 'partial'])
      .order('end_date', { ascending: true }),
    ({ data, error }) => {
      if (error) {
        console.error('Error fetching work orders:', error);
        if (onError) onError(error);
        return;
      }
      callback((data || []).map(mapWorkOrderRow));
    }
  );

  // Initial fetch
  fetcher.run();

  // Subscribe to realtime changes on work_orders table
  const channel = supabase
    .channel('work-orders-realtime')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'dispatch',
        table: 'work_orders'
      },
      () => fetcher.schedule() // Refetch (debounced) on any change
    )
    .subscribe();

  // Return unsubscribe function
  return () => {
    fetcher.close();
    supabase.removeChannel(channel);
  };
};

// Get saved work orders from database (pending AND partial status)
// Partial status means production started but not fully reported
export const getSavedWorkOrders = async () => {
  try {
    const { data, error } = await supabase
      .from('work_orders')
      .select('*')
      .in('status', ['pending', 'partial'])
      .order('end_date', { ascending: true });

    if (error) {
      throw error;
    }

    const workOrders = (data || []).map(mapWorkOrderRow);

    return {
      success: true,
      workOrders,
      count: workOrders.length,
      message: `Loaded ${workOrders.length} pending work orders`
    };
  } catch (error) {
    console.error('Error fetching saved work orders:', error);
    return {
      success: false,
      message: error.message || 'Failed to fetch saved work orders',
      workOrders: []
    };
  }
};

// Get reported work orders from database (for history view)
export const getReportedWorkOrders = async (limit = 100) => {
  try {
    const { data, error } = await supabase
      .from('work_orders')
      .select('*')
      .eq('status', 'reported')
      .order('reported_at', { ascending: false })
      .limit(limit);

    if (error) {
      throw error;
    }

    const workOrders = (data || []).map(mapWorkOrderRow);

    return {
      success: true,
      workOrders,
      count: workOrders.length,
      message: `Loaded ${workOrders.length} reported work orders`
    };
  } catch (error) {
    console.error('Error fetching reported work orders:', error);
    return {
      success: false,
      message: error.message || 'Failed to fetch reported work orders',
      workOrders: []
    };
  }
};

// Mark work orders as reported in database
export const markWorkOrdersReported = async (serialNames, reportNo = null) => {
  try {
    const updateData = {
      status: 'reported',
      reported_at: new Date().toISOString()
    };

    // Add report_no if provided
    if (reportNo) {
      updateData.report_no = reportNo;
    }

    const { error } = await supabase
      .from('work_orders')
      .update(updateData)
      .in('serial_name', serialNames);

    if (error) {
      throw error;
    }

    return {
      success: true,
      message: `Marked ${serialNames.length} work orders as reported`
    };
  } catch (error) {
    console.error('Error marking work orders as reported:', error);
    return {
      success: false,
      message: error.message || 'Failed to mark work orders as reported'
    };
  }
};
